import React, { useEffect } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";
import { classNames } from "../../Utils";
import { useTranslation } from "react-i18next";
import { generateArrayOfMonths, generateArrayOfYears } from "../../Utils";

export const filterOptions = [
  { value: "LAST_HOUR", label: "Last Hour" },
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "WEEK", label: "1 Week" },
  { value: "MONTH", label: "1 Month" },
  { value: "OTHER", label: "Other" },
];

const years = generateArrayOfYears();
const months = generateArrayOfMonths();

export const DateFilter = ({
  selectedFilter = filterOptions[1],
  setSelectedFilter,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const date = new Date();
    const currentMonth = months[date.getMonth()];
    setSelectedYear({ label: date.getFullYear(), value: date.getFullYear() });
    setSelectedMonth(currentMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col flex-wrap sm:flex-row gap-4">
      <div>
        <Listbox value={selectedFilter} onChange={setSelectedFilter}>
          {({ open }) => (
            <>
              <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                  {t(selectedFilter?.label || "Today")}
                </span>
                <SelectorIcon className="w-5 h-5 text-gray-500" />
              </ListboxButton>
              <Transition
                show={open}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {filterOptions.map((filter) => (
                    <ListboxOption
                      key={filter.value}
                      className={({ focus }) =>
                        classNames(
                          focus ? "text-white bg-blue-600" : "text-gray-900",
                          "cursor-default select-none relative py-2 pr-9"
                        )
                      }
                      value={filter}
                    >
                      {({ selected, focus }) => (
                        <>
                          <div className="flex items-center justify-between">
                            <span
                              className={classNames(
                                selected ? "font-semibold" : "font-normal",
                                "ml-3 mr-2 block truncate"
                              )}
                            >
                              {t(filter.label)}
                            </span>
                            {selected ? (
                              <span
                                className={classNames(
                                  focus ? "text-white" : "text-blue-600",
                                  "absolute inset-y-0 right-0 flex items-center pr-4"
                                )}
                              >
                                <CheckIcon className="h-5 w-5" />
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
      {selectedFilter.value === "OTHER" && (
        <>
          <div>
            <Listbox value={selectedMonth} onChange={setSelectedMonth}>
              {({ open }) => (
                <>
                  <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                    <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {t(selectedMonth?.label || "Month")}
                    </span>
                    <SelectorIcon className="w-5 h-5 text-gray-500" />
                  </ListboxButton>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                      {months.map((month) => (
                        <ListboxOption
                          key={month.value}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pr-9"
                            )
                          }
                          value={month}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate"
                                  )}
                                >
                                  {t(month.label)}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
          <div>
            <Listbox value={selectedYear} onChange={setSelectedYear}>
              {({ open }) => (
                <>
                  <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                    <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {t(selectedYear?.label || "Year")}
                    </span>
                    <SelectorIcon className="w-5 h-5 text-gray-500" />
                  </ListboxButton>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-fit rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {years.map((year) => (
                        <ListboxOption
                          key={year.value}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pr-9"
                            )
                          }
                          value={year}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate"
                                  )}
                                >
                                  {t(year.label)}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
        </>
      )}
    </div>
  );
};
